import React from 'react';
import MuiFormControl from '@mui/material/FormControl';
import MuiFormControlLabel from '@mui/material/FormControlLabel';
import MuiInputAdornment from '@mui/material/InputAdornment';
import EDTooltip from '../Tooltip/EDTooltip';
import { CommonPropsAndTypes } from '../_Types/StandardComponentProps';
import ChangedMarker from '../../Components/ChangedMarker/ChangedMarker';
import { Box } from '@mui/material';
import { SpacingProps } from '@mui/system';
import { EDSimpleCheckbox } from './SimpleCheckbox/EDSimpleCheckbox';
import EDFormHelperText from '../FormHelperText/EDFormHelperText';
import type { IEditComponentProps } from '../../Components/Attributes/EditableAttribute/EditComponentTypes';

export interface IProps extends IEditComponentProps<any> {
  labelNode?: string | React.ReactNode;
  checked?: boolean;
  disabledReason?: string;
  indeterminate?: boolean;
  flexShrink?: number;
  onCheck?: (isInputChecked: boolean, event: any) => void;
  color?: CommonPropsAndTypes.LimitedColorType;
  noClickPropagation?: boolean;
  checkboxSpacing?: SpacingProps;
  backgroundColorWhite?: boolean;
}
const noClickPropagationOnClick = (
  event: React.MouseEvent<HTMLButtonElement>
) => {
  event.stopPropagation();
};

/**
  Standard checkbox med label och tooltip
*/
const EDCheckbox: React.FC<React.PropsWithChildren<IProps>> = props => {
  const {
    label: labelString,
    labelNode,
    tooltip,
    checked,
    disabled,
    disabledReason,
    indeterminate,
    flexShrink,
    onCheck,
    testId: dataTest,
    color,
    noClickPropagation,
    marker,
    checkboxSpacing,
    backgroundColorWhite,
    helperText,
    error,
    warning,
    hint,
    onChange,
    baseOnChange,
    ...rest
  } = props;
  const label = labelString ?? labelNode ?? '';

  const onCheckboxChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void = React.useCallback<
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void
  >(
    (event, checked) => {
      baseOnChange?.(checked);
      onCheck?.(checked, event);
    },
    [baseOnChange, onCheck]
  );
  const changedMarker = (
    <Box
      component="span"
      sx={{ alignItems: 'center', display: 'flex', flexFlow: 'row nowrap' }}
    >
      <MuiInputAdornment
        sx={{
          '& .MuiInputAdornment-positionStart': {
            margin: 0,
            marginRight: 4,
          },
        }}
        position="start"
      >
        <ChangedMarker />
      </MuiInputAdornment>
      {label}
    </Box>
  );

  const renderCheckbox = () => {
    const checkbox = (
      <EDSimpleCheckbox
        onClick={
          props.noClickPropagation ? noClickPropagationOnClick : undefined
        }
        checked={!!props.checked || props.baseValue}
        disabled={!!props.disabled || !!props.masked}
        onChange={onCheckboxChange}
        indeterminate={!!props.indeterminate}
        dataTest={props.testId}
        color={props.color ? props.color : 'secondary'}
        backgroundColorWhite={props.backgroundColorWhite}
        {...checkboxSpacing}
        {...rest}
      />
    );
    if (props.hideLabel) {
      return <>{checkbox}</>;
    } else {
      return (
        <MuiFormControlLabel
          control={checkbox}
          label={props.marker ? changedMarker : label}
          sx={[
            !label && { margin: 0 },
            {
              '& .MuiFormControlLabel-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            },
          ]}
        />
      );
    }
  };

  return (
    <MuiFormControl sx={{ flexShrink: props.flexShrink }}>
      <EDTooltip
        title={
          props.disabledReason && props.disabled
            ? props.disabledReason
            : props.tooltip
        }
        enableClickOnTouch={props.disabled}
      >
        {renderCheckbox()}
      </EDTooltip>
      <EDFormHelperText
        margin="dense"
        error={props.error}
        warning={props.warning}
      >
        {props.helperText}
      </EDFormHelperText>
    </MuiFormControl>
  );
};

export default EDCheckbox;
